import { Button, Input, MagellanPopup } from 'components/atoms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlus, PiWarning, PiX } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { AsapType, IDigitalDeliveryBase } from 'services/digital-delivery/types';
import { useGetCountries } from 'services/enums';
import { ICountry } from 'services/enums/types';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { formatDateToInput } from 'utils/global/date.utils';
import WarningPopup from '../warning-popup/WarningPopup';

interface IPopupDeleteWarningProps {
  trigger?: JSX.Element;
  name?: string;
  type: 'included' | 'excluded';
  disabledCountries?: IDigitalDeliveryBase[];
  initialCountry: IDigitalDeliveryBase;
  onSave: (country: IDigitalDeliveryBase, actions?: PopupActions) => void;
}
const DigitalDeliveryCountryPopup = ({
  trigger,
  type,
  initialCountry,
  onSave,
  disabledCountries = [],
  name = '',
}: IPopupDeleteWarningProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<PopupActions>(null);

  const countries = useGetCountries();

  const [open, setOpen] = useState(false);

  const { values }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const [search, setSearch] = useState('');

  const [country, setCountry] = useState<IDigitalDeliveryBase | undefined>(initialCountry);

  useEffect(() => {
    setCountry(initialCountry);
    setSearch('');
  }, [open]);

  const { getSelectOptions } = useEnumToSelectOptions();

  const setCountryValue = (key: keyof IDigitalDeliveryBase, value: any) =>
    setCountry((prev) => prev && { ...prev, [key]: value });

  const isCountryDisabled = (c: ICountry) =>
    !!disabledCountries.find((dc) => dc.countryCode === c.codeAlfa2);

  const { minPublishDate, isReleaseDateValid } = useMemo(() => {
    const today = new Date();
    const minDate = new Date();
    minDate.setDate(today.getDate() + 10);

    const isValid = values.releaseDate
      ? new Date(values.releaseDate).getTime() >= minDate.getTime()
      : false;

    return {
      minPublishDate: formatDateToInput(minDate),
      isReleaseDateValid: isValid,
    };
  }, [values.releaseDate]);

  return (
    <Popup
      ref={popupRef}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      modal
      nested
      trigger={trigger}
    >
      <MagellanPopup>
        {type === 'excluded' && (
          <MagellanPopup.Header>
            <PiX /> {t('button.exclude')}
          </MagellanPopup.Header>
        )}
        {type === 'included' && (
          <MagellanPopup.Header>
            <PiPlus /> {t('button.select')}
          </MagellanPopup.Header>
        )}
        <div className="flex flex-col gap-4  min-h-[350px] max-h-[450px] w-full max-w-96">
          {country?.countryCode ? (
            <>
              <div className="flex flex-col w-full gap-4">
                <span />
                <div className="flex mx-4 gap-4 items-center">
                  <img
                    src={`https://flagsapi.com/${country.countryCode}/flat/32.png`}
                    className="min-w-[32px] rounded"
                  />
                  {t(`countries.${country.countryCode}`)}
                </div>
                <span />
              </div>

              <div className="flex flex-col w-full gap-2 items-center">
                <p className="font-semibold opacity-65">{t('digital-delivery.deliveryTime')}</p>
                <div className="flex w-full gap-2">
                  <Input.Select
                    appBackground
                    options={getSelectOptions(AsapType, 'enum.AsapType')}
                    onChange={(o) => setCountryValue('asap', o.value)}
                    value={{
                      label: t(`enum.AsapType.${AsapType[country.asap]}`),
                      value: country.asap,
                    }}
                    isClearable={false}
                    displayMode={false}
                    borderless
                    fullWidth
                  />
                  {!isReleaseDateValid && (
                    <WarningPopup
                      title={t('popup.header')}
                      message={t('music-product-digital.delivery.invalid-date', {
                        when: minPublishDate,
                      })}
                      trigger={
                        <Button onlyIcon variant={BUTTON_VARIANT.background}>
                          <Button.Icon icon={<PiWarning />} />
                        </Button>
                      }
                    />
                  )}
                </div>
              </div>
              {country.asap === AsapType.None && (
                <div className="flex flex-col w-full gap-2">
                  <Input.Text
                    label={t('input.label.date')}
                    name=""
                    value={country.publishDate}
                    onChange={(e) => setCountryValue('publishDate', e.target.value)}
                    type="date"
                    displayMode={false}
                    borderless
                    appBackground
                    fullWidth
                  />
                  <Input.Text
                    label={t('input.label.time')}
                    name=""
                    onChange={(e) => setCountryValue('publishTime', e.target.value)}
                    value={country.publishTime}
                    type="time"
                    displayMode={false}
                    borderless
                    fullWidth
                    appBackground
                  />
                </div>
              )}
              <div className="mt-auto" />
              {type === 'excluded' && (
                <Button
                  fullWidth
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => onSave(country, popupRef.current ?? undefined)}
                >
                  <Button.Text text={t('button.exclude')} />
                  <Button.Icon icon={<PiX />} />
                </Button>
              )}
              {type === 'included' && (
                <Button fullWidth onClick={() => onSave(country, popupRef.current ?? undefined)}>
                  <Button.Text text={t('button.select')} />
                  <Button.Icon icon={<PiPlus />} />
                </Button>
              )}
            </>
          ) : (
            <>
              <Input.Text
                name=""
                fullWidth
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                appBackground
                placeholder={t('input.placeholder.search')}
              />
              <div className="flex flex-col  overflow-auto w-full h-full">
                {countries.data
                  ?.filter((c) =>
                    search
                      ? t(`countries.${c.codeAlfa2}`).toLowerCase().includes(search.toLowerCase())
                      : true,
                  )
                  .map((c) => (
                    <div
                      key={c.codeAlfa3}
                      className={`flex flex-col w-full gap-4 ${
                        isCountryDisabled(c)
                          ? ''
                          : 'hover:bg-black hover:bg-opacity-10 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (isCountryDisabled(c)) return;
                        setCountry({
                          ...initialCountry,
                          countryCode: c.codeAlfa2,
                        });
                      }}
                    >
                      <span />
                      <div
                        className="flex mx-4 gap-4 items-center"
                        style={{
                          opacity: isCountryDisabled(c) ? 0.2 : 1,
                        }}
                      >
                        <img
                          src={`https://flagsapi.com/${c.codeAlfa2}/flat/32.png`}
                          className="min-w-[32px] rounded"
                        />
                        {t(`countries.${c.codeAlfa2}`)}
                      </div>
                      <span
                        className="w-full h-[1px] opacity-20"
                        style={{ backgroundColor: 'var(--text)' }}
                      />
                    </div>
                  ))}
              </div>{' '}
            </>
          )}
        </div>
      </MagellanPopup>
    </Popup>
  );
};

export default DigitalDeliveryCountryPopup;
